/**
 * useEvaluationContext — builds an EvaluationContext from session data.
 *
 * FIN-PS-002: Provides the canonical context for the shared fail-closed
 * condition evaluator. Permissions are server-authoritative (P0-4) and are
 * not exposed to the client; the server evaluates permissionAvailable
 * conditions before returning descriptors. The client context keeps an
 * empty permissions array so the evaluator's server-authoritative mode
 * (permissionAvailable → true when permissions is empty) is activated.
 * Enabled modules are derived from the backend navigation contract —
 * only modules with AVAILABLE routes are considered enabled.
 * Unknown/malformed conditions deny by default.
 */
import { useMemo } from 'react';
import { useBackendNavigation as useNavigation } from '../navigation/use-navigation';
import type { EvaluationContext } from './evaluate-condition';

export function useEvaluationContext(): EvaluationContext {
  const { data: contract } = useNavigation();

  return useMemo<EvaluationContext>(() => {
    const enabledModules = new Set<string>();
    const collect = (items: { module: string; availability: string }[]) => {
      for (const item of items) {
        if (item.availability === 'AVAILABLE') {
          enabledModules.add(item.module);
        }
      }
    };
    if (contract?.primary) collect(contract.primary);
    if (contract?.secondary) collect(contract.secondary);

    return {
      permissions: [],
      enabledModules: [...enabledModules],
    };
  }, [contract]);
}
